import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import type { Column } from './DataTable'
import type { TableColumnPreferences } from '@alpha-edge/shared'
import { colors } from './styles'

interface ColumnSettingsButtonProps<T> {
  readonly columns: readonly Column<T>[]
  readonly columnPrefs?: TableColumnPreferences
  readonly onColumnPrefsChange: (prefs: TableColumnPreferences) => void
}

export function ColumnSettingsButton<T>({ columns, columnPrefs, onColumnPrefsChange }: ColumnSettingsButtonProps<T>) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const dragItem = useRef<number | null>(null)
  const dragOver = useRef<number | null>(null)

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns])

  const defaultVisibleKeys = useMemo(
    () => columns.filter((c) => c.defaultVisible !== false).map((c) => c.key),
    [columns],
  )

  const orderedKeys = useMemo(() => {
    const prefOrder = columnPrefs?.order
    if (!prefOrder) return allKeys
    const allSet = new Set(allKeys)
    const ordered = prefOrder.filter((k) => allSet.has(k))
    const missing = allKeys.filter((k) => !prefOrder.includes(k))
    return [...ordered, ...missing]
  }, [columnPrefs?.order, allKeys])

  const visibleKeys = useMemo(() => {
    const prefVisible = columnPrefs?.visible
    if (!prefVisible) return new Set(defaultVisibleKeys)
    return new Set(prefVisible.filter((k) => allKeys.includes(k)))
  }, [columnPrefs?.visible, defaultVisibleKeys, allKeys])

  const columnMap = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns])

  const handleToggle = useCallback(
    (key: string) => {
      const col = columnMap.get(key)
      if (col && col.hideable === false) return
      const next = new Set(visibleKeys)
      if (next.has(key)) {
        if (next.size <= 1) return
        next.delete(key)
      } else {
        next.add(key)
      }
      onColumnPrefsChange({ visible: [...next], order: [...orderedKeys] })
    },
    [columnMap, visibleKeys, orderedKeys, onColumnPrefsChange],
  )

  const handleReorder = useCallback(
    (from: number, to: number) => {
      const next = [...orderedKeys]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      onColumnPrefsChange({ visible: [...visibleKeys], order: next })
    },
    [orderedKeys, visibleKeys, onColumnPrefsChange],
  )

  const handleReset = useCallback(() => {
    onColumnPrefsChange({ visible: undefined, order: undefined })
  }, [onColumnPrefsChange])

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Column settings"
        style={{
          background: 'none',
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          color: open ? colors.accent : colors.textMuted,
          cursor: 'pointer',
          fontSize: '0.875rem',
          padding: '0.35rem 0.5rem',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {'⚙'}
        <span style={{ fontSize: '0.8125rem' }}>Columns</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 200,
            background: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            padding: '8px 0',
            marginTop: '4px',
            minWidth: '200px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '4px 12px 8px',
              fontSize: '0.75rem',
              color: colors.textMuted,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Columns</span>
            <button
              onClick={handleReset}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                cursor: 'pointer',
                fontSize: '0.75rem',
                padding: 0,
              }}
            >
              Reset
            </button>
          </div>
          {orderedKeys.map((key, idx) => {
            const col = columnMap.get(key)
            if (!col) return null
            const canHide = col.hideable !== false
            return (
              <div
                key={key}
                draggable
                onDragStart={() => {
                  dragItem.current = idx
                }}
                onDragEnter={() => {
                  dragOver.current = idx
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={() => {
                  if (dragItem.current !== null && dragOver.current !== null && dragItem.current !== dragOver.current) {
                    handleReorder(dragItem.current, dragOver.current)
                  }
                  dragItem.current = null
                  dragOver.current = null
                }}
                style={{
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'grab',
                  fontSize: '0.8125rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.bgMuted
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = ''
                }}
              >
                <span style={{ color: '#bbb', fontSize: '0.75rem', cursor: 'grab', userSelect: 'none' }}>{'☰'}</span>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flex: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={visibleKeys.has(key)}
                    disabled={!canHide}
                    onChange={() => handleToggle(key)}
                    style={{ cursor: canHide ? 'pointer' : 'default' }}
                  />
                  {col.header}
                </label>
              </div>
            )
          })}
        </div>
      )}
    </span>
  )
}
